import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewChild,
  ComponentFactory,
  WritableSignal,
  computed,
  signal,
  Signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ActivatedRoute,
  Event as RouterEvent,
  Navigation,
  NavigationExtras,
  NavigationStart,
  Params,
  Router,
} from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import {
  catchError,
  delay,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  tap,
  withLatestFrom,
  debounceTime,
  mergeMap,
} from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import { NgbPaginationConfig } from '@ng-bootstrap/ng-bootstrap';
import * as KeyCode from 'keycode-js';
import { DsModalService, DsModalVariant } from '@levelaccess/design-system';
import { map as lodashMap, uniqBy } from 'lodash';

import { TranslateService } from '../../../../../translate/translate.service';
import { ManualAuditIssueService } from '../../../../../services/manual-audit-issue.service';
import { ModalService } from '../../../../../services/modal.service';
import { IssueReportFieldSelectorComponent } from './issue-report-field-selector/issue-report-field-selector.component';
import { ICurrentSelectedProperty, UserService } from '../../../../../services/user.service';
import { $auditIssueFields } from '../../../../../../../shared/constants/manual-audit-issues';
import { Api, ApiQueryOption } from '../../../../../../../shared/constants/api';
import {
  $userPreference,
  userPreferenceScope,
  userPreferenceScopeParam,
} from '../../../../../../../shared/constants/user-preferences';
import {
  IUserPreference,
  IUserPreferencesServerResponse,
} from '../../../../../../../shared/interfaces/user-preference.interface';
import { columnCheckboxOptions, columnDefinitionFilterType } from './constants/filtering';
import { FilterHostDirective } from '../../../../../directives/filter-host/filter-host.directive';
import { filterType, IFilterOption, IFilterOutput } from '../../../../../components/filtering/filter.interface';
import { FilterCheckboxComponent } from '../../../../../components/filtering/checkbox/filter-checkbox/filter-checkbox.component';
import { FilterKeywordComponent } from '../../../../../components/filtering/keyword/filter-keyword.component';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { MultiselectTypeaheadComponent } from '../../../../../components/multiselect-typeahead/multiselect-typeahead.component';
import { ISuccessCriteria } from '../../../../../../../shared/audits/definitions/success-criteria/success-criteria.interface';
import { $successCriteria } from '../../../../../../../shared/audits/definitions/success-criteria/constants';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { $paginationParams } from '../../../../../constants/pagination-params';
import { UserPropertyService } from '../../../../../services/user-property.service';
import { pageLoadingState } from '../../../../../components/page-loading-state/page-loading-state.component';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { DEFAULT_PAGINATION_MAX_SIZE } from '../../../../../constants/form.constants';
import {
  $auditFinding,
  ActiveAuditFindingQAStatus,
  AuditFindingQAStatus,
  AuditFindingQAStatusDisplayLabel,
  IgnoredStatusAllowedSources,
} from '../../../../../../../shared/constants/audit-finding';
import {
  IManualAuditFindingServerResponse,
  IManualAuditQuery,
  IManualFindingBulkStatusChangeRequest,
  IPopulatedManualFinding,
} from '../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { NotificationService } from '../../../../../services/notification.service';
import { NotificationPosition } from '../../../../../models/notification.model';
import { $pageWithComponents, $scan, $scanScope } from '../../../../../../../shared/constants/scan';
import { WorkspaceService } from '../../../../../services/workspace.service';
import { ITask } from '../../../../../../../shared/interfaces/task.interface';
import { IWorkspace } from '../../../../../../../shared/interfaces/workspace.interface';
import { $workspace } from '../../../../../../../shared/constants/workspace';
import { ShortLinkService } from '../../../../../services/short-link.service';
import { IssueActionModalComponent } from '../../../scans/scan-results/reports/issue-action-modal/issue-action-modal.component';
import { RuleAuditHistoryAction } from '../../../../../../../shared/constants/rule-audit-history';
import { $formFields } from './constants/form-fields';
import { BulkChangeStatusModalComponent } from './bulk-change-status-modal/bulk-change-status-modal.component';
import { IRequiredSecurity } from '../../../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../../../shared/constants/required-securities';
import { AclSecurityAdapter } from '../../../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../../shared/constants/security';
import { IScanRouteData } from '../../../../../interfaces/route-data.interface';
import { FilterNumberComponent } from '../../../../../components/filtering/number/filter-number.component';
import { TaskSource } from '../../../../../../../shared/constants/task';
import { ILinkedPropertyData, LinkedPropertyUtility } from '../../../../../../../shared/utils/linked-property.utility';
import { UserAclService } from '../../../../../services/user-acl.service';
import { SuccessCriteriaService } from '../../../../../services/success-criteria.service';
import { UpsertTaskSlideoutComponent } from '../../../projects/tasks/upsert-task-slideout/upsert-task-slideout.component';
import { BulkTaskCreationSlideoutComponent } from './bulk-task-creation-slideout/bulk-task-creation-slideout.component';
import { IMatchComponent } from '../../../../../../../shared/interfaces/match-component.interface';
import { $matchComponent } from '../../../../../../../shared/constants/match-component';
import { IManualEvaluation } from '../../../../../../../shared/interfaces/manual-audit.interface';
import { EvaluationStandards } from '../../../../../../../shared/constants/manual-evaluation';
import { SharedAuditsUtility } from '../../../../../../../shared/utils/audits.utility';
import { DEBOUNCE_TIME } from '../../../../../shared/constants';
import { FilterSelectComponent } from '../../../../../components/filtering/select/filter-select.component';
import { Origin } from '../../../../../../../shared/interfaces/scan.interface';
import { TenantSeveritiesService } from '../../../../../services/tenant-severities.service';
import { ICustomSeveritiesMap } from '../../../../../../../shared/interfaces/tenant.interface';
import { $customSeverity } from '../../../../../../../shared/constants/tenant';
import { $severity } from '../../../../../../../shared/constants/accessibility';
import { SharedTextUtility } from '../../../../../../../shared/utils/text.utility';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { BrowserSessionStorageService, SESSION_STORAGE } from '../../../../../services/browser-session-storage.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { ManualEvaluationSource } from '../../../../../../../shared/constants/manual-audit';

interface ITablePosition {
  url: string;
  page: number;
  pageSize: number;
  scrollY: number;
}

const translationKeys: Readonly<Record<string, string>> = Object.freeze({
  [$auditIssueFields.issueId]: 'issue_field_issueId',
  [$auditIssueFields.ruleId]: 'issue_field_ruleId',
  [$auditIssueFields.screen]: 'issue_field_screen',
  [$auditIssueFields.summary]: 'issue_field_summary',
  [$auditIssueFields.successCriteria]: 'issue_field_successCriteria',
  [$auditIssueFields.criteria]: 'issue_field_criteria',
  [$auditIssueFields.severity]: 'issue_field_severity',
  [$auditIssueFields.status]: 'issue_field_status',
  [$auditIssueFields.attachments]: 'issue_field_attachments',
  [$auditIssueFields.url]: 'issue_field_url',
  [$auditIssueFields.issueFrequency]: 'issue_field_issueFrequency',
  [$auditIssueFields.instances]: 'issue_field_instances',
  [$auditIssueFields.actualResult]: 'issue_field_actualResult',
  [$auditIssueFields.stepsToReproduce]: 'issue_field_stepsToReproduce',
  [$auditIssueFields.isFlagged]: 'issue_field_isFlagged',
  [$auditIssueFields.findingType]: 'issue_field_findingType',
  [$auditIssueFields.recommendation]: 'issue_field_recommendation',
  [$auditIssueFields.disabilityAffected]: 'issue_field_disabilityAffected',
  [$auditIssueFields.matchAllAffectedDisabilities]: 'match_all_disabilities_affected',
  [$auditIssueFields.multipleDisabilitiesAffected]: 'match_affecting_multiple_disabilities',
  [$auditIssueFields.conformanceLevelAndVersion]: 'issue_field_conformanceLevelAndVersion',
  [$auditIssueFields.effort]: 'issue_field_effort',
  [$auditIssueFields.createdAt]: 'issue_field_createdAt',
  [$auditIssueFields.updatedAt]: 'issue_field_updatedAt',
  [$auditIssueFields.task]: 'task',
  [$auditIssueFields.age]: 'issue_field_age',
  [$auditIssueFields.category]: 'issue_field_category',
  [$auditIssueFields.xpath]: 'issue_field_xpath',
  [$auditIssueFields.codeSnippet]: 'issue_field_codeSnippet',
  [$auditIssueFields.component]: 'issue_field_component',
  [$auditIssueFields.viewport]: 'issue_field_viewport_width',
  [$auditIssueFields.evaluationStandard]: 'label_standard',
});

const DEFAULT_AUDIT_ISSUE_FIELDS: string[] = [
  $auditIssueFields.effort,
  $auditIssueFields.summary,
  $auditIssueFields.screen,
  $auditIssueFields.criteria,
  $auditIssueFields.severity,
  $auditIssueFields.status,
];

@Component({
  selector: 'app-issues',
  templateUrl: './manual-audit-issues.component.html',
  styleUrls: ['./manual-audit-issues.component.scss'],
  standalone: false,
})
export class ManualAuditIssuesComponent implements OnInit, OnDestroy {
  private readonly previousNavigation: Navigation;
  private pendingScrollRestore: number | null = null;
  private readonly mandatoryColumns: Readonly<string[]>;
  private checkboxes: FilterCheckboxComponent<string>[];
  private multiselects: MultiselectTypeaheadComponent<string | ISuccessCriteria>[];
  private selects: FilterSelectComponent<string>[];
  private routeData$: Observable<IScanRouteData>;
  private routeDataSnapshot: IScanRouteData;
  private filteredToReviewFindings$: Observable<IPopulatedManualFinding[]>;
  private filteredToReviewFindingsCount$: Observable<number>;
  private categories: string[];
  private matchComponents: IMatchComponent[];

  private page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  private pageSize$: BehaviorSubject<number>;

  @ViewChild(FilterHostDirective)
  public filterHost: FilterHostDirective;

  @ViewChild('columnsSwitcherDropdown', { read: ElementRef })
  public columnsSwitcherDropdown: ElementRef;

  @ViewChild('bulkActionsButton')
  public bulkActionsButton: ElementRef<HTMLButtonElement>;

  public getFindings$: Subject<void> = new Subject<void>();
  public findingsData$: BehaviorSubject<IManualAuditFindingServerResponse> =
    new BehaviorSubject<IManualAuditFindingServerResponse>(undefined);
  public digitalPropertyKey$: Observable<string>;
  public paginationLabel$: Observable<string>;
  public evaluationName$: Observable<string>;
  private refreshUserPreferences$: Subject<void> = new Subject();

  public readonly entryLimits: Readonly<number[]>;

  public form: UntypedFormGroup;
  public $scan: typeof $scan = $scan;

  public filters: IManualAuditQuery;
  public filtersPending: IManualAuditQuery;
  public filtersApplied: boolean;

  public columnSelectorOpened: boolean;
  public digitalPropertyKey: WritableSignal<string> = signal('');
  public paginationLabel: WritableSignal<string> = signal('');
  public evaluationName: WritableSignal<string> = signal('');
  public optionalColumns: WritableSignal<string[]> = signal([]);
  public excludedColumns: WritableSignal<string[]> = signal([]);
  public selectedColumnNames: Signal<Set<string>> = computed<Set<string>>(() => {
    const columns: string[] = [...this.mandatoryColumns, ...this.optionalColumns()];
    // A saved selection must never surface the STAFF-only finding type column (or its filter) for other users
    const hideFindingType: boolean = this.excludedColumns().includes($auditIssueFields.findingType);
    return new Set(hideFindingType ? columns.filter((column: string) => column !== $auditIssueFields.findingType) : columns);
  });
  public evaluationStandards: Signal<EvaluationStandards[]> = computed<EvaluationStandards[]>(() => {
    const evaluation: IManualEvaluation | null = this.evaluation();
    return SharedCommonUtility.notNullish(evaluation) ? SharedAuditsUtility.getManualEvaluationStandards(evaluation) : [];
  });
  public displayBulkActions: WritableSignal<boolean> = signal(false);
  public findings: WritableSignal<IPopulatedManualFinding[]> = signal([]);
  public findingsCount: WritableSignal<number> = signal(0);
  public subscriptions: Subscription;
  public userOrigin: WritableSignal<Origin | null> = signal(null);
  public evaluation: WritableSignal<IManualEvaluation | null> = signal(null);
  public evaluationOrigin: WritableSignal<Origin | null> = signal(null);
  public evaluationSource: WritableSignal<ManualEvaluationSource | null> = signal(null);
  public exportNewFieldsFlagEnabled: WritableSignal<boolean> = signal(false);
  public asyncAiTestingEnabled: WritableSignal<boolean> = signal(false);
  private asyncAiTestingResolved: boolean = false;
  public newExportFieldsAvailable: Signal<boolean> = computed((): boolean => {
    return (
      this.exportNewFieldsFlagEnabled() &&
      this.evaluation()?.source === ManualEvaluationSource.platform &&
      SharedCommonUtility.notNullish(this.evaluation()?.scope)
    );
  });
  public manualFindingReadPermission: WritableSignal<boolean> = signal(false);
  public editPermission: WritableSignal<boolean> = signal(false);
  public dismissalPermission: WritableSignal<boolean> = signal(false);
  public manualFindingFlagCreatePermission: WritableSignal<boolean> = signal(false);
  public bulkActionsPermission: WritableSignal<boolean> = signal(false);
  public showActionsColumn: Signal<boolean> = computed<boolean>(() =>
    Boolean(this.userOrigin() && this.evaluationOrigin() && this.userOrigin() === this.evaluationOrigin()),
  );
  public removeToReviewFromClientViewEnabled: WritableSignal<boolean> = signal(false);

  public canCreateTask: boolean = false;
  public canSeeToReview: boolean;
  public selectedFindingsMap: Map<string, IPopulatedManualFinding> = new Map();
  public selectedFindings: IPopulatedManualFinding[] = [];

  public pageLoadingState: typeof pageLoadingState;
  public loadingState: pageLoadingState;

  public $formFields: typeof $formFields;

  public enableBulkActions: boolean;
  public enableBulkDismiss: boolean;
  public enableBulkIgnore: boolean;
  public enableBulkStatusChange: boolean = false;

  public readonly bulkStatusChangeSecurity: Readonly<IRequiredSecurity> = RequiredSecurities.MT_Manual_Evaluation_Update;
  public tasksCreateRequiredSecurity: IRequiredSecurity;
  public auditUpdateOrDeleteRequiredSecurity: IRequiredSecurity;
  public downloadFindingsRequiredSecurity: IRequiredSecurity;
  public showSuccessAlert: boolean;
  public customSeveritiesData: ICustomSeveritiesMap;

  protected showIgnoredStatus: boolean;

  constructor(
    private translateService: TranslateService,
    private manualAuditIssuesService: ManualAuditIssueService,
    private modalService: ModalService,
    private dsModalService: DsModalService,
    private userService: UserService,
    private userPropertyService: UserPropertyService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private activatedRoute: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private router: Router,
    private loadErrorHandler: LoadErrorHandler,
    private viewportScroller: ViewportScroller,
    private ngbPaginationConfig: NgbPaginationConfig,
    private notificationService: NotificationService,
    private workspaceService: WorkspaceService,
    private shortLinkService: ShortLinkService,
    private changeDetectorRef: ChangeDetectorRef,
    private userAclService: UserAclService,
    private successCriteriaService: SuccessCriteriaService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private featureFlagService: FeatureFlagService,
    private browserSessionStorageService: BrowserSessionStorageService,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    this.ngbPaginationConfig.maxSize = DEFAULT_PAGINATION_MAX_SIZE;

    this.mandatoryColumns = [$auditIssueFields.issueId, $auditIssueFields.task];
    this.previousNavigation = this.router.getCurrentNavigation()?.previousNavigation;
    this.checkboxes = [];
    this.multiselects = [];
    this.selects = [];
    this.entryLimits = [20, 50, 100];
    this.pageSize$ = new BehaviorSubject<number>(this.entryLimits[1]);
    this.restoreTablePosition();
    this.columnSelectorOpened = false;

    this.filters = {};
    this.filtersPending = {};
    this.filtersApplied = false;

    this.canSeeToReview = false;

    this.pageLoadingState = pageLoadingState;
    this.loadingState = pageLoadingState.loading;

    this.subscriptions = new Subscription();
    this.enableBulkActions = false;
    this.enableBulkDismiss = false;
    this.tasksCreateRequiredSecurity = RequiredSecurities.PM_Tasks_Create;
    this.downloadFindingsRequiredSecurity = RequiredSecurities.MT_Manual_Evaluation_Download;

    this.auditUpdateOrDeleteRequiredSecurity = RequiredSecurities.MT_Manual_Evaluation_Update_Delete;

    this.categories = [];
    this.matchComponents = [];
  }

  private clearSelection(): void {
    this.selectedFindingsMap.clear();
    this.checkForLinkedTasks();
  }

  private getUserPrefs(): void {
    const onSuccess = (prefs: IUserPreferencesServerResponse): void => {
      const selectedColPrefs: IUserPreference = prefs.userPreferences.find(
        (p: IUserPreference): boolean => p.param === userPreferenceScopeParam.selectedColumns,
      );
      if (this.filtersLength === 0 && this.previousNavigation !== null) {
        const filterPrefs: IUserPreference = prefs.userPreferences.find(
          (p: IUserPreference): boolean => p.param === userPreferenceScopeParam.issueFilter,
        );

        const filters: IManualAuditQuery = filterPrefs?.[$userPreference.value] ?? {
          [$auditIssueFields.status]: ActiveAuditFindingQAStatus.filter(
            (status: AuditFindingQAStatus) => status !== AuditFindingQAStatus.dismissed,
          ),
        };
        this.filters = this.normalizeCriteriaFilter(filters);
        this.filtersPending = Object.assign({}, this.filters);
        this.removeFindingTypeFromSelectedFilters();
      }

      if (Array.isArray(selectedColPrefs?.[$userPreference.value])) {
        this.updateSelectedColumns(this.transformSelectedColumns(selectedColPrefs[$userPreference.value]));
      } else {
        this.updateSelectedColumns(DEFAULT_AUDIT_ISSUE_FIELDS);
      }
      this.getFindings$.next();
    };

    this.subscriptions.add(
      this.userService.getUserPreferences(userPreferenceScope.manualAuditIssues).subscribe({
        next: onSuccess,
        error: (error: HttpErrorResponse): void => {
          this.handleError('error_while_getting_your_user_preferences', error);
        },
      }),
    );
  }

  private transformSelectedColumns(columns: string[]): string[] {
    return columns.map((column: string) => {
      if (column === $auditIssueFields.conformanceLevelAndVersion) {
        return $auditIssueFields.evaluationStandard;
      }
      if (column === $auditIssueFields.successCriteria) {
        return $auditIssueFields.criteria;
      }
      return column;
    });
  }

  private buildObservables(): void {
    this.digitalPropertyKey$ = this.userPropertyService.currentSelectedProperty().pipe(
      delay(0),
      take(1),
      map((property: ICurrentSelectedProperty) => property.digitalPropertyKey),
    );

    const findingsData$: Observable<IManualAuditFindingServerResponse> = this.getFindings$.pipe(
      mergeMap(this.updateUrlFromFilterData.bind(this)),
      withLatestFrom(this.routeData$),
      switchMap(([, routeData]: [void, IScanRouteData]): Observable<IManualAuditFindingServerResponse> => {
        return this.manualAuditIssuesService.getEvaluationIssues(
          routeData?.[Api.workspaceId],
          routeData?.[Api.digitalPropertyId],
          routeData.scanId,
          this.filterParams,
        );
      }),
      tap(() => {
        this.filtersApplied = true;
        this.loadingState = pageLoadingState.loaded;
      }),
      catchError((error: HttpErrorResponse): Observable<any> => {
        this.handleError('error_getting_manual_audit_issues', error);
        this.loadingState = pageLoadingState.error;
        return of(null);
      }),
    );

    this.subscriptions.add(findingsData$.subscribe(this.findingsData$));

    this.paginationLabel$ = combineLatest([this.findingsCount$, this.page$, this.pageSize$]).pipe(
      delay(0),
      map(([findingsCount, page, pageSize]: [number, number, number]) => {
        const startIndex = Math.min((page - 1) * pageSize + 1, findingsCount);
        const lastIndex = Math.min(startIndex + pageSize - 1, findingsCount);
        return this.translateService.instant('n_of_t', [`${startIndex} - ${lastIndex}`, String(findingsCount)]);
      }),
    );

    const permissionsCheck$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(this.auditUpdateOrDeleteRequiredSecurity.functionalActions)
            .check(),
        ),
      );
    this.subscriptions.add(
      permissionsCheck$.subscribe((hasPermissions: boolean) => {
        this.bulkActionsPermission.set(hasPermissions);
        this.updateDisplayBulkActions();
      }),
    );

    const manualFindingReadPermission$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(RequiredSecurities.MT_Manual_Finding_Review_Read.functionalActions)
            .check(),
        ),
      );
    this.subscriptions.add(
      manualFindingReadPermission$.subscribe((hasPermission: boolean) => {
        this.manualFindingReadPermission.set(hasPermission);
        this.updateExcludedColumns();
      }),
    );
    this.subscriptions.add(
      this.featureFlagService.variation$(FeatureFlagCollection.manualEvaExportNewFields, false).subscribe((value: boolean) => {
        this.exportNewFieldsFlagEnabled.set(value);
      }),
    );
    this.subscriptions.add(
      this.featureFlagService.variation$(FeatureFlagCollection.asyncAiTesting, false).subscribe((value: boolean) => {
        this.asyncAiTestingEnabled.set(value);
        this.asyncAiTestingResolved = true;
        this.updateExcludedColumns();
      }),
    );
    let initialFlagEmission = true;
    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.removeToReviewFromClientView, false)
        .subscribe((value: boolean) => {
          this.removeToReviewFromClientViewEnabled.set(value);
          this.recalculateCanSeeToReview();
          // Skip the initial emission: URL filter params are not yet loaded and a premature
          // fetch would overwrite them.
          if (!initialFlagEmission && this.userOrigin() === Origin.client) {
            this.getFindings$.next();
          }
          initialFlagEmission = false;
        }),
    );
  }

  private static withoutSuccessAlert(url: string): string {
    const parsed = new URL(url, 'http://localhost');
    parsed.searchParams.delete(ApiQueryOption.showSuccessAlert);
    return parsed.pathname + parsed.search;
  }

  private static getPath(url: string): string {
    return url.split(/[?#]/)[0];
  }

  private saveTablePositionOnLeave(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event: RouterEvent): event is NavigationStart => event instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          if (ManualAuditIssuesComponent.getPath(event.url) === ManualAuditIssuesComponent.getPath(this.router.url)) {
            return;
          }
          const position: ITablePosition = {
            url: this.router.url,
            page: this.page$.value,
            pageSize: this.pageSize$.value,
            scrollY: this.findScrollContainer().scrollTop,
          };
          this.browserSessionStorageService.setItem(SESSION_STORAGE.MANUAL_AUDIT_ISSUES_TABLE_POSITION, position);
        }),
    );
  }

  private restoreTablePosition(): void {
    const position: ITablePosition | null = this.browserSessionStorageService.getItem<ITablePosition>(
      SESSION_STORAGE.MANUAL_AUDIT_ISSUES_TABLE_POSITION,
    );
    this.browserSessionStorageService.removeItem(SESSION_STORAGE.MANUAL_AUDIT_ISSUES_TABLE_POSITION);
    const targetUrl: string = this.router.getCurrentNavigation()?.extractedUrl?.toString() ?? this.router.url;
    if (
      SharedCommonUtility.isNullish(position) ||
      ManualAuditIssuesComponent.withoutSuccessAlert(position.url) !== ManualAuditIssuesComponent.withoutSuccessAlert(targetUrl)
    ) {
      return;
    }
    this.pageSize$.next(position.pageSize);
    this.page$.next(position.page);
    this.pendingScrollRestore = position.scrollY;
  }

  private restoreScrollOnceFindingsRender(findings: IPopulatedManualFinding[]): void {
    if (this.pendingScrollRestore === null || findings.length === 0) {
      return;
    }
    const scrollY: number = this.pendingScrollRestore;
    this.pendingScrollRestore = null;
    const applyScroll = (): void => {
      const scroller: HTMLElement = this.findScrollContainer();
      const previousScrollBehavior: string = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTop = scrollY;
      scroller.style.scrollBehavior = previousScrollBehavior;
    };
    setTimeout(applyScroll);
    setTimeout(applyScroll, 250);
  }

  private findScrollContainer(): HTMLElement {
    let node: HTMLElement | null = this.elementRef.nativeElement;
    while (node !== null && node !== document.body && node !== document.documentElement) {
      const overflowY: string = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return (document.scrollingElement as HTMLElement) ?? document.documentElement;
  }

  private connectSignals(): void {
    this.subscriptions.add(this.digitalPropertyKey$.subscribe((value: string) => this.digitalPropertyKey.set(value ?? '')));
    this.subscriptions.add(this.paginationLabel$.subscribe((value: string) => this.paginationLabel.set(value ?? '')));
    this.subscriptions.add(this.evaluationName$.subscribe((value: string) => this.evaluationName.set(value ?? '')));
    this.subscriptions.add(
      this.findings$.subscribe((findings: IPopulatedManualFinding[]) => {
        this.findings.set(findings ?? []);
        this.restoreScrollOnceFindingsRender(findings ?? []);
      }),
    );
    this.subscriptions.add(this.findingsCount$.subscribe((count: number) => this.findingsCount.set(count ?? 0)));
  }

  private updateDisplayBulkActions(): void {
    const hasPermissions = this.bulkActionsPermission();
    const userOrigin = this.userOrigin();
    const evalOrigin = this.evaluationOrigin();
    this.displayBulkActions.set(Boolean(hasPermissions && userOrigin && evalOrigin && userOrigin === evalOrigin));
  }

  private updateExcludedColumns(): void {
    const origin = this.userOrigin();
    const hasPermission = this.manualFindingReadPermission();
    const isStaff = origin === Origin.staff;
    const excluded: string[] = isStaff && hasPermission ? [] : [$auditIssueFields.isFlagged];
    if (!isStaff || !this.asyncAiTestingEnabled()) {
      excluded.push($auditIssueFields.findingType);
    }
    this.excludedColumns.set(excluded);
    this.removeFindingTypeFromSelectedFilters();
    if (this.filterHost && this.optionalColumns().length > 0) {
      // the flag/role resolve asynchronously, after the saved column selection may already have been applied
      this.updateSelectedColumnsFilters(this.selectedColumnNames());
    }
  }

  private updateShowIgnoredStatus(): void {
    const hasEdit = this.editPermission();
    const origin = this.userOrigin();
    const source = this.evaluationSource();
    const isStaff = origin === Origin.staff;
    const hasAllowedSource = SharedCommonUtility.notNullish(source) && IgnoredStatusAllowedSources.includes(source);
    this.showIgnoredStatus = Boolean(hasEdit && isStaff && hasAllowedSource);
  }

  private recalculateCanSeeToReview(): void {
    const hasEditPermission = this.editPermission();
    const ffEnabled = this.removeToReviewFromClientViewEnabled();
    const origin = this.userOrigin();
    const isClient = origin === Origin.client;
    const newValue = hasEditPermission && !(ffEnabled && isClient);
    const changed = this.canSeeToReview !== newValue;

    this.canSeeToReview = newValue;

    if (!newValue && isClient && ffEnabled) {
      this.removeToReviewFromSelectedFilters();
    }

    // Rebuild after updating canSeeToReview and cleaning filters so the UI reflects the
    // current state.
    if (changed && this.filterHost) {
      this.updateSelectedColumnsFilters(this.selectedColumnNames());
    }
  }

  private removeToReviewFromSelectedFilters(): void {
    const statusFilterKey = $auditIssueFields.status;

    [this.filters, this.filtersPending].forEach((filterObj: Record<string, AuditFindingQAStatus[] | undefined>) => {
      const statusFilter = filterObj[statusFilterKey];
      if (statusFilter) {
        const filtered = statusFilter.filter((status: AuditFindingQAStatus) => status !== AuditFindingQAStatus.toReview);
        if (filtered.length > 0) {
          filterObj[statusFilterKey] = filtered;
        } else {
          // Remove the key entirely so it no longer counts as an active filter.
          delete filterObj[statusFilterKey];
        }
      }
    });
  }

  private removeFindingTypeFromSelectedFilters(): void {
    const findingTypeKey = $auditIssueFields.findingType;
    // The role and the flag resolve asynchronously: only drop the filter once both are known, so a STAFF user's filter survives
    const resolved: boolean = this.userOrigin() !== null && this.asyncAiTestingResolved;
    if (!resolved || !this.excludedColumns().includes(findingTypeKey)) {
      return;
    }
    if (this.filters[findingTypeKey] === undefined && this.filtersPending[findingTypeKey] === undefined) {
      return;
    }
    const wasApplied: boolean = this.filters[findingTypeKey] !== undefined;
    delete this.filters[findingTypeKey];
    delete this.filtersPending[findingTypeKey];

    if (wasApplied) {
      // The hidden filter was still being sent to the server, and may be persisted in the user preferences and the URL
      this.saveFilterToPreference(this.filters);
      this.getFindings$.next();
      this.subscriptions.add(this.updateUrlFromFilterData().subscribe());
    }
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError(message, response, ManualAuditIssuesComponent.name);
  }

  private subscribeToPropertyChange(): void {
    this.subscriptions.add(
      this.routeData$
        .pipe(
          pairwise(),
          filter(([oldData, newData]: [IScanRouteData, IScanRouteData]): boolean => {
            return (
              oldData[Api.workspaceId] !== newData[Api.workspaceId] ||
              oldData[Api.digitalPropertyId] !== newData[Api.digitalPropertyId] ||
              oldData[Api.scanId] !== newData[Api.scanId]
            );
          }),
        )
        .subscribe((): void => {
          this.router
            .navigate(['/', Api.manual_evaluations], {
              replaceUrl: true,
            })
            .then();
        }),
    );
  }

  private updateSelectedColumns(newSelections: string[]): void {
    this.optionalColumns.set(newSelections);
    this.updateSelectedColumnsFilters(this.selectedColumnNames());
  }

  private updateSelectedColumnsFilters(selectedColumns: Set<string>): void {
    const [sortBy] = ManualAuditIssuesComponent.getSortingOrderFromQueryParams(this.activatedRoute.snapshot.queryParams);
    if (!SharedCommonUtility.isNullish(sortBy) && !Array.from(selectedColumns).includes(sortBy)) {
      this.updateUrlSortByAndDirection(null, null);
    }

    this.loadFilterComponents(Array.from(selectedColumns));
  }

  private getColumnCheckboxOptions(column: string): IFilterOption<string>[] {
    const options: IFilterOption<string>[] = columnCheckboxOptions[column];

    if (column === $auditIssueFields.status && this.showIgnoredStatus) {
      // This status is not available for everyone so we add it dynamically if the user can see it
      return [
        ...options,
        {
          label: AuditFindingQAStatusDisplayLabel[AuditFindingQAStatus.ignored],
          value: AuditFindingQAStatus.ignored,
        },
      ];
    }

    if (column === $auditIssueFields.status && !this.canSeeToReview) {
      return options.filter((status: IFilterOption<any>) => status.value !== AuditFindingQAStatus.toReview);
    }

    if (column === $auditIssueFields.severity) {
      const severitiesOrderedByRank: $severity[] = this.tenantSeveritiesService.getSeverityKeysSortedByRank(
        this.customSeveritiesData,
      );
      return severitiesOrderedByRank.map((severity: $severity) => ({
        label: SharedTextUtility.capitalize(this.customSeveritiesData.get(severity)[$customSeverity.label]),
        value: severity,
      }));
    }
    return options;
  }

  private buildEditPermissionCheck(): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useEntityLevel()
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useFunctionalActions({ [FunctionalArea.mt_manual_evaluations]: [SecureAction.update] })
          .check(),
      ),
    );
  }

  private buildDismissalPermissionCheck(): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useEntityLevel()
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useFunctionalActions({ [FunctionalArea.mt_manual_evaluations]: [SecureAction.delete] })
          .check(),
      ),
    );
  }

  private buildManualFindingFlagCreatePermissionCheck(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(RequiredSecurities.MT_Manual_Finding_Review_Create.functionalActions)
            .check(),
        ),
      );
  }

  private loadFilterComponents(columns: string[]): void {
    const viewContainerRef = this.filterHost.viewContainerRef;
    viewContainerRef.clear();

    const keywordColumns: string[] = [];
    const checkboxColumns: string[] = [];
    const multiselectColumns: string[] = [];
    const numberColumns: string[] = [];
    const selectColumns: string[] = [];

    for (const column of columns) {
      const columnFilterType: filterType = columnDefinitionFilterType[column];
      switch (columnFilterType) {
        case filterType.checkbox:
          checkboxColumns.push(column);
          break;
        case filterType.keyword:
          keywordColumns.push(column);
          break;
        case filterType.number:
          numberColumns.push(column);
          break;
        case filterType.multiselect:
          multiselectColumns.push(column);
          break;
        case filterType.select:
          selectColumns.push(column);
          break;
        // no default
      }
    }

    for (const column of keywordColumns) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory<FilterKeywordComponent<string>>(FilterKeywordComponent);

      const componentRef = viewContainerRef.createComponent<FilterKeywordComponent<string>>(componentFactory);
      componentRef.instance.label = translationKeys[column];
      componentRef.instance.field = column;
      componentRef.instance.value = this.filters[column] as string;
      this.subscriptions.add(componentRef.instance.valueChange.subscribe(this.setFilterValue.bind(this)));

      const element: HTMLElement = componentRef.location.nativeElement as HTMLElement;
      element.classList.add('margin-left-md');
      element.classList.add('margin-bottom-md');
      element.style.width = '336px';
      element.style.maxWidth = '336px';
    }

    for (const column of numberColumns) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory<FilterNumberComponent<string>>(FilterNumberComponent);

      const componentRef = viewContainerRef.createComponent<FilterNumberComponent<string>>(componentFactory);
      componentRef.instance.label = translationKeys[column];
      componentRef.instance.field = column;
      componentRef.instance.value = this.filters[column] as string;
      this.subscriptions.add(componentRef.instance.valueChange.subscribe(this.setFilterValue.bind(this)));

      const element: HTMLElement = componentRef.location.nativeElement as HTMLElement;
      element.classList.add('margin-left-md');
      element.classList.add('margin-bottom-md');
      element.style.width = '336px';
      element.style.maxWidth = '336px';
    }

    for (const column of multiselectColumns) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory<MultiselectTypeaheadComponent<string>>(
          MultiselectTypeaheadComponent,
        );

      const componentRef =
        viewContainerRef.createComponent<MultiselectTypeaheadComponent<string | ISuccessCriteria>>(componentFactory);
      componentRef.instance.label = translationKeys[column];
      componentRef.instance.formControlName = column;
      switch (column) {
        case $auditIssueFields.category:
          componentRef.instance.possibleValues = this.categories;
          break;
        case $auditIssueFields.criteria:
        case $auditIssueFields.successCriteria: {
          const successCriteriaToValueFn = (sc: ISuccessCriteria): string => sc?.[$successCriteria.num];
          const successCriteriaToLabelFn = (sc: ISuccessCriteria): string =>
            `${sc?.[$successCriteria.num]} ${sc?.[$successCriteria.handle]}`;

          if (column === $auditIssueFields.criteria) {
            componentRef.instance.possibleValues = [...this.successCriteriaService.getAllSupportedCriteriaFromStandard()];
          } else {
            componentRef.instance.possibleValues = [...this.successCriteriaService.getAllWCAGWithNACriteriaFromStandard()];
          }

          componentRef.instance.valueToSelectedItemLabelFunction = successCriteriaToLabelFn;
          componentRef.instance.valueToOutputValueFunction = successCriteriaToValueFn;
          componentRef.instance.valueToDropdownLabelFunction = successCriteriaToLabelFn;
          break;
        }
        default:
          componentRef.instance.possibleValues = [];
          componentRef.instance.allowCustomValues = true;
          break;
      }

      componentRef.instance.values = this.filters[column] ? (this.filters[column] as string[]) : [];
      componentRef.instance.registerOnChange((selectedValues: string[]) => {
        this.setFilterValue({
          field: column,
          values: selectedValues,
        });
      });
      this.multiselects.push(componentRef.instance);

      const element: HTMLElement = componentRef.location.nativeElement as HTMLElement;
      element.classList.add('margin-left-md');
      element.classList.add('margin-bottom-md');
      element.style.width = '336px';
      element.style.maxWidth = '336px';
    }

    for (const column of selectColumns) {
      const componentFactory: ComponentFactory<FilterSelectComponent<string>> =
        this.componentFactoryResolver.resolveComponentFactory<FilterSelectComponent<string>>(FilterSelectComponent);

      const componentRef: ComponentRef<FilterSelectComponent<string>> =
        viewContainerRef.createComponent<FilterSelectComponent<string>>(componentFactory);
      componentRef.instance.field = column;

      if (column === $auditIssueFields.component) {
        componentRef.instance.options = [
          {
            label: `-- ${this.translateService.instant('select_an_option')} --`,
            value: '',
          },
          {
            label: this.translateService.instant('issue_field_component_no_component_assigned_option'),
            value: 'null',
          },
          ...this.matchComponents.map((matchComponent: IMatchComponent) => ({
            label: matchComponent[$matchComponent.name],
            value: matchComponent[$matchComponent._id],
          })),
        ];
      }
      componentRef.instance.label = translationKeys[column];
      componentRef.instance.value = this.filters[column] ?? '';
      this.subscriptions.add(
        componentRef.instance.valueChange
          .pipe(filter((filterValue: IFilterOutput<string>): boolean => SharedCommonUtility.notNullish(filterValue.values)))
          .subscribe(this.setFilterValue.bind(this)),
      );

      this.selects.push(componentRef.instance);

      const element: HTMLElement = componentRef.location.nativeElement as HTMLElement;
      element.classList.add('margin-left-md');
      element.classList.add('margin-bottom-md');
      element.style.width = '336px';
      element.style.maxWidth = '336px';
    }

    for (const column of checkboxColumns) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory<FilterCheckboxComponent<string>>(FilterCheckboxComponent);

      const componentRef = viewContainerRef.createComponent<FilterCheckboxComponent<string>>(componentFactory);
      componentRef.instance.label = translationKeys[column];
      componentRef.instance.field = column;
      componentRef.instance.value = this.filters[column] ? (this.filters[column] as string[]) : [];
      componentRef.instance.options = this.getColumnCheckboxOptions(column);
      componentRef.instance.ngOnChanges({
        options: new SimpleChange(null, componentRef.instance.options, true),
        value: new SimpleChange(null, componentRef.instance.value, true),
      });

      this.checkboxes.push(componentRef.instance);

      this.subscriptions.add(componentRef.instance.valueChange.subscribe(this.setFilterValue.bind(this)));

      const element: HTMLElement = componentRef.location.nativeElement as HTMLElement;
      element.classList.add('margin-left-md');
      element.classList.add('margin-bottom-md');
      element.style.width = '336px';
      element.style.maxWidth = '336px';
    }
  }

  private saveFilterToPreference(queryFilter: IManualAuditQuery): void {
    const onUpdatePreferenceSuccess = (): void => {};

    const onUpdatePreferencesError = (error: HttpErrorResponse): void => {
      this.handleError('error_while_saving_user_preferences', error);
    };

    this.subscriptions.add(
      this.userService
        .updateUserPreferences(userPreferenceScope.manualAuditIssues, { [userPreferenceScopeParam.issueFilter]: queryFilter })
        .subscribe(onUpdatePreferenceSuccess, onUpdatePreferencesError),
    );
  }

  private setFilterValue(queryFilter: IFilterOutput<any>): void {
    this.filtersApplied = false;
    if (queryFilter.values.length === 0) {
      delete this.filtersPending[queryFilter.field];
      return;
    }
    this.filtersPending[queryFilter.field] = queryFilter.values;
  }

  private updateUrlFromFilterData(): Observable<any> {
    const queryParams: Record<string, string> = this.filterParams as Record<string, string>;

    const [requestedSortBy, requestedDirection] = ManualAuditIssuesComponent.getSortingOrderFromQueryParams(
      this.activatedRoute.snapshot.queryParams,
    );
    if (requestedSortBy) {
      queryParams[$paginationParams.sortBy] = requestedSortBy;
      queryParams[$paginationParams.direction] = requestedDirection;
    }
    queryParams[ApiQueryOption.linkedPropertyData] = this.activatedRoute.snapshot.queryParams[ApiQueryOption.linkedPropertyData];
    queryParams[ApiQueryOption.showSuccessAlert] = this.activatedRoute.snapshot.queryParams[ApiQueryOption.showSuccessAlert];

    const routeParams: NavigationExtras = {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      replaceUrl: true,
    };

    return fromPromise(this.router.navigate([], routeParams)).pipe(
      catchError(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService)),
    );
  }

  private updateUrlSortByAndDirection(sortBy: string, direction: string): void {
    const routeParams: NavigationExtras = {
      relativeTo: this.activatedRoute,
      queryParams: {
        [$paginationParams.sortBy]: sortBy,
        [$paginationParams.direction]: direction,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    };
    this.router.navigate([], routeParams).then();
  }

  private createSingleTask(finding: IPopulatedManualFinding): void {
    this.subscriptions.add(
      this.workspaceService
        .getWorkspace(this.routeDataSnapshot.workspaceId)
        .pipe(switchMap((workspace: IWorkspace): EventEmitter<ITask> => this.openSlideout(workspace, finding)))
        .subscribe(() => {
          this.clearSelection();
          this.getFindings$.next();
        }),
    );
  }

  private openSlideout(workspace: IWorkspace, finding: IPopulatedManualFinding): EventEmitter<ITask> {
    const description: string = this.generateTaskDescription(workspace, finding);

    const component: UpsertTaskSlideoutComponent = this.dsModalService.open(
      UpsertTaskSlideoutComponent,
      DsModalVariant.slideOut,
    ).componentInstance;

    component.taskSource = TaskSource.manual;
    component.taskName = finding[$auditFinding.summary];
    component.description = description;
    component.auditFindings = [finding[$auditFinding._id]];
    component.workspaceId = workspace[$workspace._id] || '';
    component.attachments = finding[$auditFinding.attachment] || [];
    component.notifyChanges = true;
    component.modalInit(true);

    return component.onUpsert;
  }

  private generateTaskDescription(workspace: IWorkspace, finding: IPopulatedManualFinding): string {
    const baseUrl: string = this.shortLinkService.getBaseUrl(workspace[$workspace.tenant]);

    const linkToFinding: string = this.router
      .createUrlTree([finding[$auditFinding.issueId], Api.view], {
        relativeTo: this.activatedRoute,
        queryParams: this.activatedRoute.snapshot.queryParams,
      })
      .toString();
    return this.manualAuditIssuesService.generateTaskDescription(baseUrl, finding, linkToFinding);
  }

  private createMultipleTasks(findings: IPopulatedManualFinding[]): void {
    const modalRef: BulkTaskCreationSlideoutComponent = this.dsModalService.open(
      BulkTaskCreationSlideoutComponent,
      DsModalVariant.slideOut,
    ).componentInstance;
    modalRef.findings = findings;
    modalRef.workspaceId = this.routeDataSnapshot.workspaceId;
    modalRef.activatedRoute = this.activatedRoute;

    this.subscriptions.add(
      modalRef.created.subscribe(() => {
        this.clearSelection();
        this.getFindings$.next();
      }),
    );
  }

  private static getFilterDataFromUrlQuery(queryParams: Params): IManualAuditQuery {
    const filters: IManualAuditQuery = {};

    for (const param in queryParams) {
      if (typeof queryParams[param] === 'string' && queryParams[param].length > 0) {
        const paramValue: string = queryParams[param];
        if (columnDefinitionFilterType[param] === undefined) {
          continue;
        }
        switch (columnDefinitionFilterType[param]) {
          case filterType.keyword:
          case filterType.number:
          case filterType.hidden:
            filters[param] = paramValue;
            break;
          case filterType.checkbox:
          case filterType.multiselect:
            filters[param] = paramValue.split(',').map(window.decodeURIComponent);
            break;
          // no default
        }
      }
    }

    return filters;
  }

  private static getSortingOrderFromQueryParams(params: Params): [string, string] {
    return [params[$paginationParams.sortBy], params[$paginationParams.direction]];
  }

  private get filterParams(): IManualAuditQuery {
    const filterParams: IManualAuditQuery = {
      skipScanStandards: 'true',
    };
    for (const queryFilter in this.filters) {
      filterParams[queryFilter] =
        this.filters[queryFilter] instanceof Array
          ? this.filters[queryFilter].map(window.encodeURIComponent).join(',')
          : this.filters[queryFilter];
    }
    return filterParams;
  }

  private subscribeToPermissionChecks(): void {
    this.subscriptions.add(
      this.buildEditPermissionCheck().subscribe((hasAccess: boolean) => {
        this.editPermission.set(hasAccess);
        this.canSeeToReview = hasAccess;
        this.updateShowIgnoredStatus();
      }),
    );
    this.subscriptions.add(
      this.buildDismissalPermissionCheck().subscribe((hasAccess: boolean) => {
        this.dismissalPermission.set(hasAccess);
      }),
    );
    this.subscriptions.add(
      this.buildManualFindingFlagCreatePermissionCheck().subscribe((hasAccess: boolean) => {
        this.manualFindingFlagCreatePermission.set(hasAccess);
      }),
    );
  }

  private loadRuleCategories(): void {
    this.subscriptions.add(
      this.routeData$
        .pipe(
          take(1),
          switchMap((routeData: IScanRouteData): Observable<string[]> =>
            this.manualAuditIssuesService.getRuleCategories(routeData.workspaceId, routeData.digitalPropertyId, routeData.scanId),
          ),
        )
        .subscribe((categories: string[]): void => {
          this.categories = categories;
          this.refreshUserPreferences$.next();
        }),
    );
  }

  private loadEvaluationMatchComponents(): void {
    this.subscriptions.add(
      this.routeData$
        .pipe(
          take(1),
          switchMap((routeData: IScanRouteData): Observable<IManualEvaluation> =>
            this.manualAuditIssuesService.getManualEvaluation(
              routeData[Api.workspaceId],
              routeData[Api.digitalPropertyId],
              routeData[Api.scanId],
            ),
          ),
        )
        .subscribe((evaluation: IManualEvaluation): void => {
          const allComponents: IMatchComponent[] = lodashMap(
            evaluation?.scope?.[$scanScope.pages],
            $pageWithComponents.components,
          ).flat();
          this.matchComponents = uniqBy(allComponents, $matchComponent._id);
          this.refreshUserPreferences$.next();
        }),
    );
  }

  @HostListener('document:keydown', ['$event'])
  public onTabNavigation(event: KeyboardEvent): boolean {
    const target: EventTarget = event.target;

    if (event.keyCode === KeyCode.KEY_TAB && this.columnSelectorOpened) {
      const focusable: NodeListOf<HTMLElement> = this.columnsSwitcherDropdown?.nativeElement.querySelectorAll(
        'a, button, input, select, textarea',
      );
      const prevTargetIndex: number = Array.from(focusable).indexOf(target as HTMLElement);
      if (focusable.length > 0 && (prevTargetIndex === -1 || prevTargetIndex >= focusable.length - 1)) {
        focusable[0].focus();
      }
    }

    return true;
  }

  public applyFilters(): void {
    this.filters = Object.assign({}, this.filtersPending);

    const errorUpdatingUrl = (error: any): void => {
      console.error(`${ManualAuditIssuesComponent.name}.errorUpdatingUrl`, error);
    };

    this.viewportScroller.scrollToPosition([0, 0]);
    this.saveFilterToPreference(this.filters);
    this.clearSelection();
    this.getFindings$.next();
    this.subscriptions.add(this.updateUrlFromFilterData().subscribe({ error: errorUpdatingUrl }));
  }

  public clearAllFilters(): void {
    this.filters = {};
    this.filtersPending = {};
    this.filtersApplied = false;
    this.clearCheckboxes();
    this.clearMultiselects();
    this.clearSelects();
    const columns: Set<string> = this.selectedColumnNames();
    this.loadFilterComponents(Array.from(columns));
    this.applyFilters();
  }

  public closeFilters(): void {
    this.resetCheckboxValues();
  }

  public clearCheckboxes(): void {
    this.checkboxes.forEach((checkbox: FilterCheckboxComponent<string>) => {
      checkbox.value = [];
      checkbox.resetToInitialValue();
    });
  }

  public clearMultiselects(): void {
    this.multiselects.forEach((multiselect: MultiselectTypeaheadComponent<string>) => multiselect.clear());
  }

  public clearSelects(): void {
    this.selects.forEach((selectComponent: FilterSelectComponent<string>) => {
      selectComponent.form.reset();
    });
  }

  public resetCheckboxValues(): void {
    this.checkboxes.forEach((checkbox: FilterCheckboxComponent<string>) => {
      checkbox.resetToInitialValue();
    });
    this.filtersApplied = true;
    this.filtersPending = Object.assign({}, this.filters);
  }

  public get filtersLength(): number {
    return Object.getOwnPropertyNames(this.filtersPending).length;
  }

  public get filterLabels(): string[] {
    const labels: string[] = [];
    for (const queryFilter in this.filters) {
      if (queryFilter === $auditIssueFields.multipleDisabilitiesAffected) {
        const label: string =
          this.filters[queryFilter] === 'true' ? 'match_affecting_multiple_disabilities' : 'match_affecting_single_disability';
        labels.push(label);
        continue;
      }

      labels.push(translationKeys[queryFilter]);
    }
    return labels;
  }

  public get findings$(): Observable<IPopulatedManualFinding[]> {
    return this.filteredToReviewFindings$;
  }

  public get findingsCount$(): Observable<number> {
    return this.filteredToReviewFindingsCount$;
  }

  public get paginationCollectionSize(): number | null {
    return this.pendingScrollRestore !== null && this.loadingState !== pageLoadingState.loaded ? null : this.findingsCount();
  }

  public set page(value: number) {
    this.page$.next(value);
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  public get page(): number {
    return this.page$.value;
  }

  public set pageSize(value: number) {
    this.pageSize$.next(value);
  }

  public get pageSize(): number {
    return this.pageSize$.value;
  }

  public onSelectedColumnsChange(newSelections: string[]): void {
    this.columnSelectorOpened = false;
    this.updateSelectedColumns(newSelections);

    const onUpdatePreferencesError = (error: HttpErrorResponse): void => {
      this.handleError('error_while_saving_user_preferences', error);
    };

    this.subscriptions.add(
      this.userService
        .updateUserPreferences(userPreferenceScope.manualAuditIssues, {
          [userPreferenceScopeParam.selectedColumns]: newSelections,
        })
        .subscribe({ error: onUpdatePreferencesError.bind(this) }),
    );
  }

  public openFieldSelectorModal(issueCount: number): void {
    if (this.columnSelectorOpened) {
      this.toggleColumnSelector();
    }

    const modalComponentRef: ComponentRef<IssueReportFieldSelectorComponent> = this.modalService.open(
      IssueReportFieldSelectorComponent,
    );

    modalComponentRef.instance.modalInputData = {
      data: {
        issueCount: issueCount,
        scanId: this.routeDataSnapshot.scanId,
        filterQuery: this.filterParams,
        newFieldsAvailable: this.newExportFieldsAvailable(),
      },
    };
  }

  public toggleColumnSelector(): void {
    this.columnSelectorOpened = !this.columnSelectorOpened;
  }

  public pageDataLoaded(): boolean {
    return this.loadingState === pageLoadingState.loaded;
  }

  public checkForLinkedTasks(): void {
    this.selectedFindings = Array.from(this.selectedFindingsMap.values());
    const isEveryFindingNotLinked = this.selectedFindings.every((finding: IPopulatedManualFinding): boolean =>
      SharedCommonUtility.isNullish(finding[$auditFinding.task]),
    );
    const isEveryFindingNotDismissed = this.selectedFindings.every(
      (finding: IPopulatedManualFinding): boolean => finding[$auditFinding.QAStatus] !== AuditFindingQAStatus.dismissed,
    );
    const isEveryFindingNotLinkedAndNotDismissed = isEveryFindingNotLinked && isEveryFindingNotDismissed;

    this.canCreateTask = this.selectedFindings.length > 0 && isEveryFindingNotLinkedAndNotDismissed;
    this.enableBulkActions = this.selectedFindings.length > 1;
    this.enableBulkDismiss = this.enableBulkActions && isEveryFindingNotLinkedAndNotDismissed;
    this.enableBulkIgnore = isEveryFindingNotLinked;
    this.enableBulkStatusChange = this.enableBulkActions && isEveryFindingNotDismissed;

    this.changeDetectorRef.detectChanges();
  }

  public createTask(): void {
    if (this.selectedFindings.length === 1) {
      this.createSingleTask(this.selectedFindings[0]);
    } else {
      this.createMultipleTasks(this.selectedFindings);
    }
  }

  public createTaskButtonLabel(): string {
    const onlyWithoutTasks = (task: IPopulatedManualFinding): boolean => {
      return SharedCommonUtility.getTypeOf(task[$auditFinding.task]) !== 'object';
    };
    const countWithoutTasks: number = this.selectedFindings.filter(onlyWithoutTasks).length;

    if (countWithoutTasks === 1) {
      return this.translateService.instant('create_n_task', [countWithoutTasks]);
    }

    return countWithoutTasks > 0
      ? this.translateService.instant('create_n_tasks', [countWithoutTasks])
      : this.translateService.instant('label_create_task');
  }

  public openBulkDismissModal(): void {
    const modalRef: ComponentRef<IssueActionModalComponent> = this.modalService.open(
      IssueActionModalComponent,
      this.bulkActionsButton.nativeElement,
    );
    modalRef.instance.action = RuleAuditHistoryAction.bulkDismiss;
    modalRef.instance.reasonIsRequired = true;

    const onDismissSuccess = (): void => {
      this.clearSelection();
      this.getFindings$.next();

      const message: string = this.translateService.instant('findings_dismiss_success');
      this.notificationService.success(message, NotificationPosition.Toast, true);
    };

    const onDismissError = (): void => {
      const message: string = this.translateService.instant('findings_dismiss_error');
      this.notificationService.error(message, NotificationPosition.Toast, true);
    };

    const onModalClose = (): void => {
      const formData: FormData = new FormData();
      formData.append($formFields.reason, modalRef.instance.form.get($formFields.reason).value);
      for (const finding of this.selectedFindings) {
        formData.append($formFields.manualAuditIssueIds, finding[$auditFinding.issueId]);
      }

      this.subscriptions.add(
        this.manualAuditIssuesService
          .dismissQAIssues(
            formData,
            this.routeDataSnapshot.workspaceId,
            this.routeDataSnapshot.scanId,
            this.routeDataSnapshot.digitalPropertyId,
          )
          .subscribe({
            next: onDismissSuccess.bind(this),
            error: onDismissError.bind(this),
          }),
      );
    };

    this.subscriptions.add(
      modalRef.instance.container.onClose$
        .pipe(
          take(1),
          filter((): boolean => modalRef.instance.confirmed),
        )
        .subscribe({
          next: onModalClose.bind(this),
          error: onDismissError.bind(this),
        }),
    );
  }

  public openBulkStatusChangeModal(): void {
    const modal: BulkChangeStatusModalComponent = this.modalService.open(
      BulkChangeStatusModalComponent,
      this.bulkActionsButton.nativeElement,
    ).instance;
    const propertyData: ILinkedPropertyData = LinkedPropertyUtility.fromLinkedPropertyQueryParam(
      this.activatedRoute.snapshot.queryParams,
    );

    modal.metadata = {
      workspaceId: propertyData[Api.workspaceId],
      digitalPropertyId: propertyData[Api.digitalPropertyId],
      scanId: this.activatedRoute.snapshot.paramMap.get(Api.scanId),
    };
    modal.findings = this.selectedFindings;

    modal.statusChange
      .pipe(take(1))
      .toPromise()
      .then((statusChanged: boolean) => {
        this.clearSelection();
        if (statusChanged) {
          this.getFindings$.next();
        }
      });
  }

  private onBulkIgnoreSuccess(): void {
    this.clearSelection();
    this.getFindings$.next();
    const message = this.translateService.instant('findings_ignore_success');
    this.notificationService.success(message, NotificationPosition.Toast, true);
  }

  private onBulkIgnoreError(): void {
    const message = this.translateService.instant('findings_ignore_error');
    this.notificationService.error(message, NotificationPosition.Toast, true);
  }

  public onBulkIgnore(): void {
    const findingIds: string[] = this.selectedFindings
      .filter((finding: IPopulatedManualFinding) => finding[$auditFinding.QAStatus] !== AuditFindingQAStatus.ignored)
      .map((finding: IPopulatedManualFinding) => finding[$auditFinding.issueId]);

    if (findingIds.length > 0) {
      const changeRequest: IManualFindingBulkStatusChangeRequest = {
        manualAuditIssueIds: findingIds,
        issueStatus: AuditFindingQAStatus.ignored,
      };

      this.subscriptions.add(
        this.manualAuditIssuesService
          .bulkUpdateFindingStatuses(
            this.routeDataSnapshot.workspaceId,
            this.routeDataSnapshot.digitalPropertyId,
            this.routeDataSnapshot.scanId,
            changeRequest,
          )
          .subscribe({
            next: () => this.onBulkIgnoreSuccess(),
            error: () => this.onBulkIgnoreError(),
          }),
      );
    } else {
      const message: string = this.translateService.instant('findings_ignore_warning');
      this.notificationService.warn(message, NotificationPosition.Toast, true);
    }
  }

  public onSuccessAlertClosed(): void {
    this.showSuccessAlert = false;
  }

  public normalizeCriteriaFilter(filters: IManualAuditQuery): IManualAuditQuery {
    const newFilters = { ...filters };
    if ($auditIssueFields.successCriteria in newFilters) {
      newFilters[$auditIssueFields.criteria] = newFilters[$auditIssueFields.successCriteria];
      delete newFilters[$auditIssueFields.successCriteria];
    }
    return newFilters;
  }

  public ngOnInit(): void {
    this.saveTablePositionOnLeave();
    this.routeData$ = combineLatest([
      this.activatedRoute.params.pipe(map((params: Params): string => params[Api.scan_id])),
      this.activatedRoute.queryParams.pipe(map(LinkedPropertyUtility.fromLinkedPropertyQueryParam)),
    ]).pipe(
      map(([scanId, propertyData]: [string, ILinkedPropertyData]): IScanRouteData => {
        return {
          [Api.scanId]: scanId,
          ...propertyData,
        };
      }),
    );

    this.subscriptions.add(
      this.userService.getUserOrigin().subscribe((origin: Origin) => {
        this.userOrigin.set(origin);
        this.updateDisplayBulkActions();
        this.updateExcludedColumns();
        this.updateShowIgnoredStatus();
        this.recalculateCanSeeToReview();
      }),
    );

    this.subscriptions.add(
      this.routeData$
        .pipe(
          switchMap((routeData: IScanRouteData): Observable<IScanRouteData> =>
            this.manualAuditIssuesService
              .getManualEvaluation(routeData[Api.workspaceId], routeData[Api.digitalPropertyId], routeData[Api.scanId])
              .pipe(
                tap((evaluation: IManualEvaluation) => {
                  this.evaluation.set(evaluation);
                  this.evaluationOrigin.set(evaluation?.origin ?? null);
                  this.evaluationSource.set(evaluation?.source ?? null);
                  this.updateDisplayBulkActions();
                  this.updateShowIgnoredStatus();
                }),
                map(() => routeData),
              ),
          ),
        )
        .subscribe((routeData: IScanRouteData) => {
          this.routeDataSnapshot = routeData;
        }),
    );

    this.subscriptions.add(
      combineLatest([
        this.tenantSeveritiesService.getAll(),
        this.refreshUserPreferences$.pipe(debounceTime(DEBOUNCE_TIME)),
      ]).subscribe(([customSeverities, _]: [ICustomSeveritiesMap, void]) => {
        this.customSeveritiesData = customSeverities;

        const filtersInQuery: IManualAuditQuery = ManualAuditIssuesComponent.getFilterDataFromUrlQuery(
          this.activatedRoute.snapshot.queryParams,
        );
        if (Object.getOwnPropertyNames(filtersInQuery).length > 0) {
          this.filters = this.normalizeCriteriaFilter(filtersInQuery);
          this.filtersPending = Object.assign({}, this.filters);
          // e.g. a shared link carrying &findingType=... for a user who cannot see that filter
          this.removeFindingTypeFromSelectedFilters();
          this.saveFilterToPreference(this.filters);
        }

        this.getUserPrefs();
      }),
    );

    this.loadRuleCategories();
    this.loadEvaluationMatchComponents();

    this.evaluationName$ = this.findingsData$.pipe(map((data: IManualAuditFindingServerResponse) => data?.evaluationName ?? ''));

    this.subscribeToPropertyChange();

    this.subscribeToPermissionChecks();

    const toReviewFilter = (finding: IPopulatedManualFinding): boolean => {
      if (this.canSeeToReview) {
        return true;
      }
      return finding[$auditFinding.QAStatus] !== AuditFindingQAStatus.toReview;
    };
    this.filteredToReviewFindings$ = this.findingsData$.pipe(
      map((data: IManualAuditFindingServerResponse) => {
        return data?.findings?.filter(toReviewFilter);
      }),
    );
    this.filteredToReviewFindingsCount$ = this.findingsData$.pipe(
      map((data: IManualAuditFindingServerResponse) => {
        return data?.findings?.filter(toReviewFilter).length || 0;
      }),
    );

    this.buildObservables();
    this.connectSignals();

    this.showSuccessAlert = this.activatedRoute.snapshot.queryParams[ApiQueryOption.showSuccessAlert] === 'true';
    if (this.showSuccessAlert) {
      // remove the 'showSuccessAlert' queryParam
      this.router.navigate([], {
        queryParams: { [ApiQueryOption.showSuccessAlert]: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
